import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Box,
  Stack,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  CircularProgress,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  useMediaQuery,
  Tooltip,
  Chip,
} from "@mui/material";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ConfirmationDialog from "../../dialogs/ConfirmationDialog";
import {
  deleteApplication,
  editApplicationTitle,
  getApplicationTitles,
  toggleApplicationPin,
} from "../../actions/applicationActions";
import { showInsufficientCreditsModal } from "../../actions/creditActions";
import { deductCredits } from "../../utils/HelperFunction/deductCredits";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBarsStaggered } from "@fortawesome/free-solid-svg-icons";
import {
  Plus,
  Edit3,
  Trash2,
  X,
  Check,
  MoreHorizontal,
  ChevronDown,
  ChevronLeft,
  Menu as MenuIcon,
  Search,
  FileText,
  Calendar,
  Clock,
  History,
  Pin,
  PinOff,
} from "lucide-react";
import LogoLoader from "../UI/LogoLoader";

const Sidebar = ({
  isCollapsed = false,
  toggleSidebar,
  setIsHistoryClicked,
  setApplicationToEdit,
  setGeneratedApplication,
  setReadOnly,
}) => {
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const { documentId } = useParams();
  const dispatch = useDispatch();
  const credits = useSelector((state) => state.credit.credits);
  const userId = useSelector((state) => state.credit.userId);
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const applicationTitles = useSelector((state) => state.applicationTitles);
  const { loading, titles } = applicationTitles;

  const [selectedChat, setSelectedChat] = useState({ chatId: "", title: "" });
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [isEditing, setIsEditing] = useState({ chatId: "" });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const filteredData = titles && titles?.filter((item) => item.Title !== null);

  const groupedData =
    filteredData && Object.groupBy(filteredData, (title) => title?.Category);

  const open = Boolean(anchorEl);
  const inputRef = useRef(null);

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleNewChatClick = () => {
    setSelectedChat({ chatId: "", title: "" });
    setExpanded(null);
    setIsHistoryClicked(false);
    setApplicationToEdit({});
    setReadOnly(false);
    dispatch({ type: "NEW_APPLICATION_REQUEST" });
    dispatch({ type: "SELECTED_APPLICATION_RESET" });
    isMobileView && toggleSidebar();
  };

  const handleInputChange = (e) => {
    const cursorPosition = inputRef.current.selectionStart;
    setNewTitle(e.target.value);

    // Set the cursor back to where it was
    setTimeout(() => {
      inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
    }, 0);
  };

  const handleSaveEdit = (chatId) => {
    if (!newTitle) return;
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal());
      return;
    }

    dispatch(editApplicationTitle(userInfo._id, chatId, newTitle));

    // Reset editing state after saving
    setIsEditing({ chatId: "" });
    setNewTitle("");

    // Deduct credits
    const deductionSuccess = deductCredits(
      {
        userId,
        creditsToDeduct: 1,
        event: "Application title edit on application drafting page.",
      },
      dispatch,
    );

    if (!deductionSuccess) {
      console.warn(
        "Failed to deduct credits. Ensure retry or user notification.",
      );
    }
  };

  const handleCancelEdit = (index) => {
    setIsEditing({ chatId: "" });
    setNewTitle("");
  };

  const selectApplication = (application) => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal());
      return;
    }
    setIsEditing({ chatId: "" });
    setSelectedChat({ chatId: application.chat_id, title: application.Title });
    setAnchorEl(null);
    setReadOnly(true);
    setGeneratedApplication("");
    dispatch({ type: "SET_SELECTED_APPLICATION", payload: application });
    setIsHistoryClicked(false);
    setApplicationToEdit({});

    // Deduct credits
    const deductionSuccess = deductCredits(
      {
        userId,
        creditsToDeduct: 1,
        event: "Application preview on application drafting page.",
      },
      dispatch,
    );

    if (!deductionSuccess) {
      console.warn(
        "Failed to deduct credits. Ensure retry or user notification.",
      );
    }
  };

  const handleListItemClick = (application) => {
    selectApplication(application);
    isMobileView && toggleSidebar();
  };

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditTitle = (event, chatId) => {
    event.stopPropagation();
    setIsEditing({ chatId });
    const findChat = titles?.find((title) => title.chat_id === chatId);
    setNewTitle(findChat.Title);
  };

  const handleTogglePin = (event, chatId) => {
    event.stopPropagation();
    const findChat = titles?.find((title) => title.chat_id === chatId);
    dispatch(toggleApplicationPin(userInfo?._id, chatId, !findChat?.isPinned));
    setAnchorEl(null);
  };

  const handleConfirmDelete = (chatId) => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal());
      return;
    }
    dispatch(deleteApplication(userInfo?._id, chatId));
    setSelectedChat({ chatId: "", title: "" });
    dispatch({ type: "SELECTED_APPLICATION_RESET" });

    // Close the delete dialog
    setOpenDeleteDialog(false);

    // Deduct credits
    const deductionSuccess = deductCredits(
      {
        userId,
        creditsToDeduct: 1,
        event: "Delete application on application drafting page.",
      },
      dispatch,
    );

    if (!deductionSuccess) {
      console.warn(
        "Failed to deduct credits. Ensure retry or user notification.",
      );
    }
  };

  const handleDeleteChat = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(null);

    setOpenDeleteDialog(true);
  };

  const handleAccordionToggle = (category) => {
    setExpanded((prev) => {
      if (prev === category) {
        setSelectedChat({ chatId: "", title: "" });
        dispatch({ type: "SELECTED_APPLICATION_RESET" });
        return null;
      } else {
        return category;
      }
    });
  };

  useEffect(() => {
    dispatch(getApplicationTitles("application", userInfo?._id));
    return () => {
      dispatch({ type: "SELECTED_APPLICATION_RESET" });
    };
  }, []);

  const hasHandledDeepLink = useRef(false);

  useEffect(() => {
    if (!documentId || !titles?.length || hasHandledDeepLink.current) return;
    const match = titles.find((item) => item.chat_id === documentId);
    if (match) {
      hasHandledDeepLink.current = true;
      setExpanded(match.Category);
      selectApplication(match);
    }
  }, [documentId, titles]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        ...(isMobileView
          ? { width: "320px" }
          : {
              flexBasis: isCollapsed ? "60px" : "320px",
              flexShrink: 0,
              maxWidth: isCollapsed ? "60px" : "320px",
              transition: "flex-basis 0.3s ease",
            }),
        height: "100%",
        boxSizing: "border-box",
        margin: "12px",
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        border: "1px solid #F1F5F9",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
        overflow: "hidden",
        fontFamily:
          "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      <Box
        sx={{
          display: "flex",
          height: "100%",
          boxSizing: "border-box",
          flexDirection: "column",
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        {/*Side bar Header */}
        <Box
          sx={{
            padding: "20px 24px",
            borderBottom: "1px solid #F1F5F9",
            backgroundColor: "#FFFFFF",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "16px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "40px",
                  height: "40px",
                  backgroundColor: "#F0F9FF",
                  borderRadius: "12px",
                  color: "#0073A6",
                }}
              >
                <FileText size={20} />
              </Box>
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#12243C",
                  fontFamily:
                    "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                Applications
              </Typography>
            </Box>
            {!isMobileView && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Tooltip title="New Application" placement="top">
                  <IconButton
                    onClick={handleNewChatClick}
                    sx={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      backgroundColor: "#F0F9FF",
                      color: "#0073A6",
                      "&:hover": {
                        backgroundColor: "#E0F2FE",
                        transform: "scale(1.05)",
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                  >
                    <Plus size={16} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="View History" placement="top">
                  <IconButton
                    onClick={() => {
                      setIsHistoryClicked(true);
                      setSelectedChat({ chatId: "", title: "" });
                      setExpanded(null);
                      isMobileView && toggleSidebar();
                    }}
                    sx={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      backgroundColor: "#FEF3C7",
                      color: "#D97706",
                      "&:hover": {
                        backgroundColor: "#FDE68A",
                        transform: "scale(1.05)",
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                  >
                    <History size={16} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>
        {isMobileView && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "16px 24px",
                borderBottom: "1px solid #F1F5F9",
              }}
            >
              <IconButton
                size="small"
                sx={{
                  display: "flex",
                  width: "40px",
                  height: "40px",
                  borderRadius: "10px",
                  backgroundColor: "#F0F9FF",
                  color: "#0073A6",
                  "&:hover": {
                    backgroundColor: "#E0F2FE",
                  },
                }}
                onClick={toggleSidebar}
              >
                <FontAwesomeIcon icon={faBarsStaggered} fontSize="16px" />
              </IconButton>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "40px",
                    height: "40px",
                    backgroundColor: "#F0F9FF",
                    borderRadius: "12px",
                    color: "#0073A6",
                  }}
                >
                  <FileText size={20} />
                </Box>
                <Typography
                  sx={{
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#12243C",
                    fontFamily:
                      "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  }}
                >
                  Applications
                </Typography>
              </Box>
            </Box>
            <Box sx={{ padding: "16px 24px" }}>
              <Tooltip title="View History">
                <IconButton
                  size="small"
                  onClick={() => {
                    setIsHistoryClicked(true);
                    setSelectedChat({ chatId: "", title: "" });
                    setExpanded(null);
                    isMobileView && toggleSidebar();
                  }}
                  sx={{
                    width: "36px",
                    height: "36px",
                    backgroundColor: "#F8FAFC",
                    color: "#64748B",
                    borderRadius: "8px",
                    "&:hover": {
                      backgroundColor: "#F1F5F9",
                    },
                  }}
                >
                  <History size={16} />
                </IconButton>
              </Tooltip>
            </Box>
          </>
        )}
        {isCollapsed && (
          <Box sx={{ padding: "16px 12px" }}>
            <Tooltip title="New Application" placement="right">
              <IconButton
                sx={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "8px",
                  backgroundColor: "#F0F9FF",
                  color: "#0073A6",
                  marginBottom: "8px",
                  "&:hover": {
                    backgroundColor: "#E0F2FE",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
                onClick={handleNewChatClick}
              >
                <Plus size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title="View History" placement="right">
              <IconButton
                sx={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "8px",
                  backgroundColor: "#FEF3C7",
                  color: "#D97706",
                  "&:hover": {
                    backgroundColor: "#FDE68A",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
                onClick={() => {
                  setIsHistoryClicked(true);
                  setSelectedChat({ chatId: "", title: "" });
                  setExpanded(null);
                  isMobileView && toggleSidebar();
                }}
              >
                <History size={16} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            padding: "16px 0",
            "::-webkit-scrollbar": {
              width: "6px",
            },
            "::-webkit-scrollbar-track": {
              background: "#F8FAFC",
            },
            "::-webkit-scrollbar-thumb": {
              background: "#CBD5E1",
              borderRadius: "8px",
              "&:hover": {
                background: "#94A3B8",
              },
            },
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "40px 20px",
              }}
            >
              <LogoLoader size={isMobileView ? "sm" : "md"} duration="1s" />
            </Box>
          ) : titles?.length > 0 && !isCollapsed ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                padding: "0 16px",
              }}
            >
              {Object.entries(groupedData)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([category, items]) => (
                  <Accordion
                    key={category}
                    expanded={expanded === category}
                    onChange={() => handleAccordionToggle(category)}
                    sx={{
                      margin: "4px 0",
                      borderRadius: "10px",
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
                      border: "1px solid #F1F5F9",
                      "&:before": { display: "none" },
                      "&.Mui-expanded": {
                        margin: "4px 0px",
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ChevronDown size={16} color="#64748B" />}
                      aria-controls={`${category}-content`}
                      id={`${category}-header`}
                      sx={{
                        width: "100%",
                        overflow: "hidden",
                        backgroundColor: "#F8FAFC",
                        borderRadius: "10px",
                        padding: "12px 16px",
                        minHeight: "48px",
                        "&.Mui-expanded": {
                          minHeight: "48px",
                          padding: "12px 16px",
                          backgroundColor: "#F0F9FF",
                        },
                        ".MuiAccordionSummary-content": {
                          margin: "0px",
                          "&.Mui-expanded": {
                            margin: "0px",
                          },
                        },
                        "&:hover": {
                          backgroundColor: "#F1F5F9",
                        },
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: "600",
                            fontSize: "14px",
                            color: "#12243C",
                            fontFamily:
                              "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                          }}
                        >
                          {category}
                        </Typography>
                        <Chip
                          label={items?.length}
                          size="small"
                          sx={{
                            height: "20px",
                            fontSize: "11px",
                            fontWeight: "500",
                            backgroundColor: "#E0F2FE",
                            color: "#0073A6",
                            fontFamily:
                              "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                          }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        padding: "8px 0px",
                        backgroundColor: "#FFFFFF",
                        maxHeight: "240px",
                        overflowY: "auto",
                        "::-webkit-scrollbar": {
                          width: "4px",
                        },
                        "::-webkit-scrollbar-track": {
                          background: "#F8FAFC",
                        },
                        "::-webkit-scrollbar-thumb": {
                          background: "#CBD5E1",
                          borderRadius: "4px",
                          "&:hover": {
                            background: "#94A3B8",
                          },
                        },
                      }}
                    >
                      {[...(items || [])]
                        .sort(
                          (a, b) => (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0),
                        )
                        .map((item, index) => (
                          <Box key={item.chat_id}>
                            <ListItem disablePadding>
                              <ListItemButton
                                disableRipple
                                sx={{
                                  width: "100%",
                                  margin: "2px 8px",
                                  borderRadius: "8px",
                                  height: "40px",
                                  padding: "8px 12px",
                                  "&.Mui-selected": {
                                    backgroundColor: "#F0F9FF",
                                    "&:hover": {
                                      backgroundColor: "#E0F2FE",
                                    },
                                  },
                                  "&:hover": {
                                    backgroundColor: "#F8FAFC",
                                  },
                                  transition: "all 0.2s ease-in-out",
                                }}
                                selected={selectedChat.chatId === item.chat_id}
                                onClick={() => handleListItemClick(item)}
                              >
                                {isEditing.chatId === item.chat_id ? (
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    width="100%"
                                  >
                                    <input
                                      ref={inputRef}
                                      style={{
                                        width: "100%",
                                        fontSize: "14px",
                                        padding: "6px 8px",
                                        border: "1px solid #E2E8F0",
                                        borderRadius: "6px",
                                        outline: "none",
                                        fontFamily:
                                          "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                      }}
                                      type="text"
                                      autoFocus
                                      value={newTitle}
                                      onClick={(e) => e.stopPropagation()}
                                      onFocus={(e) => e.stopPropagation()}
                                      onChange={handleInputChange}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSaveEdit(item.chat_id);
                                        setAnchorEl(null);
                                      }}
                                      sx={{
                                        width: "24px",
                                        height: "24px",
                                        backgroundColor: "#F0FDF4",
                                        color: "#16A34A",
                                        "&:hover": {
                                          backgroundColor: "#DCFCE7",
                                        },
                                      }}
                                    >
                                      <Check size={14} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCancelEdit();
                                        setAnchorEl(null);
                                      }}
                                      sx={{
                                        width: "24px",
                                        height: "24px",
                                        backgroundColor: "#FEF2F2",
                                        color: "#DC2626",
                                        "&:hover": {
                                          backgroundColor: "#FEE2E2",
                                        },
                                      }}
                                    >
                                      <X size={14} />
                                    </IconButton>
                                  </Stack>
                                ) : (
                                  <Stack
                                    direction="row"
                                    width="100%"
                                    justifyContent="space-between"
                                    alignItems="center"
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        flex: 1,
                                        minWidth: 0,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          width: "24px",
                                          height: "24px",
                                          borderRadius: "6px",
                                          backgroundColor: "#F0F9FF",
                                          color: "#0073A6",
                                          flexShrink: 0,
                                        }}
                                      >
                                        <FileText size={12} />
                                      </Box>
                                      <Typography
                                        noWrap
                                        sx={{
                                          textOverflow: "ellipsis",
                                          overflow: "hidden",
                                          whiteSpace: "nowrap",
                                          fontSize: "14px",
                                          color: "#12243C",
                                          fontFamily:
                                            "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                        }}
                                      >
                                        {item.Title}
                                      </Typography>
                                      {item.isPinned && (
                                        <Pin
                                          size={12}
                                          color="#0073A6"
                                          style={{ flexShrink: 0 }}
                                        />
                                      )}
                                    </Box>
                                    {selectedChat.chatId === item.chat_id && (
                                      <>
                                        <IconButton
                                          size="small"
                                          id="basic-button"
                                          aria-controls={
                                            open ? "basic-menu" : undefined
                                          }
                                          aria-haspopup="true"
                                          aria-expanded={
                                            open ? "true" : undefined
                                          }
                                          onClick={handleMenuOpen}
                                          sx={{
                                            width: "24px",
                                            height: "24px",
                                            color: "#64748B",
                                            "&:hover": {
                                              backgroundColor: "#F1F5F9",
                                              color: "#12243C",
                                            },
                                            transition: "all 0.2s ease-in-out",
                                          }}
                                        >
                                          <MoreHorizontal size={14} />
                                        </IconButton>
                                        <Menu
                                          anchorEl={anchorEl}
                                          id="basic-menu"
                                          open={open}
                                          onClose={handleMenuClose}
                                          PaperProps={{
                                            sx: {
                                              borderRadius: "10px",
                                              boxShadow:
                                                "0 4px 20px rgba(0, 0, 0, 0.1)",
                                              border: "1px solid #F1F5F9",
                                              mt: 1,
                                            },
                                          }}
                                        >
                                          <MenuItem
                                            onClick={(e) =>
                                              handleTogglePin(e, item.chat_id)
                                            }
                                            sx={{
                                              fontSize: "14px",
                                              fontFamily:
                                                "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                              color: "#12243C",
                                              "&:hover": {
                                                backgroundColor: "#F0F9FF",
                                              },
                                            }}
                                          >
                                            {item.isPinned ? (
                                              <PinOff
                                                size={16}
                                                style={{
                                                  marginRight: "8px",
                                                  color: "#64748B",
                                                }}
                                              />
                                            ) : (
                                              <Pin
                                                size={16}
                                                style={{
                                                  marginRight: "8px",
                                                  color: "#0073A6",
                                                }}
                                              />
                                            )}
                                            {item.isPinned ? "Unpin" : "Pin"}
                                          </MenuItem>
                                          <MenuItem
                                            onClick={(e) =>
                                              handleEditTitle(e, item.chat_id)
                                            }
                                            sx={{
                                              fontSize: "14px",
                                              fontFamily:
                                                "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                              color: "#12243C",
                                              "&:hover": {
                                                backgroundColor: "#F0F9FF",
                                              },
                                            }}
                                          >
                                            <Edit3
                                              size={16}
                                              style={{
                                                marginRight: "8px",
                                                color: "#0073A6",
                                              }}
                                            />
                                            Edit
                                          </MenuItem>
                                          <MenuItem
                                            onClick={(e) => handleDeleteChat(e)}
                                            sx={{
                                              fontSize: "14px",
                                              fontFamily:
                                                "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                              color: "#DC2626",
                                              "&:hover": {
                                                backgroundColor: "#FEF2F2",
                                              },
                                            }}
                                          >
                                            <Trash2
                                              size={16}
                                              style={{ marginRight: "8px" }}
                                            />
                                            Delete
                                          </MenuItem>
                                        </Menu>
                                      </>
                                    )}
                                  </Stack>
                                )}
                              </ListItemButton>
                            </ListItem>
                            {/* Divider */}
                            {index !== items.length - 1 && (
                              <Divider
                                sx={{
                                  margin: "4px 16px",
                                  backgroundColor: "#F1F5F9",
                                }}
                              />
                            )}
                          </Box>
                        ))}
                    </AccordionDetails>
                  </Accordion>
                ))}
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                padding: "40px 20px",
              }}
            >
              {!isCollapsed && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "16px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "64px",
                      height: "64px",
                      borderRadius: "16px",
                      backgroundColor: "#F8FAFC",
                      color: "#64748B",
                    }}
                  >
                    <FileText size={32} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#12243C",
                      fontFamily:
                        "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                      textAlign: "center",
                    }}
                  >
                    No Applications Yet
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#64748B",
                      fontFamily:
                        "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                      textAlign: "center",
                      maxWidth: "300px",
                      lineHeight: "1.5",
                    }}
                  >
                    Get started with your legal drafting. Create a new
                    application today!
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
      <ConfirmationDialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        title="Delete Application"
        description={`This will delete "${selectedChat?.title}". You won't be able to recover it.`}
        onConfirm={() => handleConfirmDelete(selectedChat?.chatId)}
        confirmText="Delete"
        confirmColor="error"
      />
    </Box>
  );
};

export default Sidebar;
